/**
 * Phase 7 — violation-monitoring canonical constants.
 *
 * Mirrors the shape of the work-order and compliance constants files:
 * literal arrays drive both Postgres enums (via Drizzle `pgEnum`) and
 * Zod parsers across API + worker + web. Adding or removing a value
 * requires a coordinated migration; this file is the cross-process
 * source of truth.
 */

import type { Borough } from './constants.js';

// ---------- Agencies ----------

export const VIOLATION_AGENCIES = [
  'HPD',
  'DOB',
  'ECB',
  'OATH',
  'FDNY',
  'DOHMH',
  'DOT',
  '_311',
] as const;
export type ViolationAgency = (typeof VIOLATION_AGENCIES)[number];

export const VIOLATION_AGENCY_LABELS: Record<ViolationAgency, string> = {
  HPD: 'HPD',
  DOB: 'DOB',
  ECB: 'ECB',
  OATH: 'OATH',
  FDNY: 'FDNY',
  DOHMH: 'DOHMH',
  DOT: 'DOT',
  _311: '311',
};

// ---------- Severity ----------

/**
 * Violation severity has an extra `info` band that work orders don't
 * (e.g. raw 311 complaints, dismissed OATH cases). The canonical color
 * mapping in `DESIGN_SYSTEM.md` §9 covers `critical|high|medium|low`;
 * `info` reuses `--color-sev-info`.
 */
export const VIOLATION_SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'] as const;
export type ViolationSeverity = (typeof VIOLATION_SEVERITIES)[number];

export const VIOLATION_SEVERITY_LABELS: Record<ViolationSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
};

/** Numeric rank for "≥ threshold" comparisons. Higher = more severe. */
export const VIOLATION_SEVERITY_RANK: Record<ViolationSeverity, number> = {
  info: 0,
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

export function severityAtLeast(
  candidate: ViolationSeverity,
  threshold: ViolationSeverity,
): boolean {
  return VIOLATION_SEVERITY_RANK[candidate] >= VIOLATION_SEVERITY_RANK[threshold];
}

// ---------- Status ----------

export const VIOLATION_STATUSES = [
  'open',
  'closed',
  'in_review',
  'dismissed',
  'unknown',
] as const;
export type ViolationStatus = (typeof VIOLATION_STATUSES)[number];

export const VIOLATION_STATUS_LABELS: Record<ViolationStatus, string> = {
  open: 'Open',
  closed: 'Closed',
  in_review: 'In review',
  dismissed: 'Dismissed',
  unknown: 'Unknown',
};

export const VIOLATION_OPEN_STATUSES: readonly ViolationStatus[] = [
  'open',
  'in_review',
];

// ---------- Event log kinds ----------

export const VIOLATION_EVENT_KINDS = [
  'first_observed',
  'status_changed',
  'penalty_changed',
  'cure_date_changed',
  'acknowledged',
  'dismissed',
  'auto_wo_created',
  'manual_link_to_wo',
  'unlinked_from_wo',
  'agency_update',
] as const;
export type ViolationEventKind = (typeof VIOLATION_EVENT_KINDS)[number];

export const VIOLATION_EVENT_ACTOR_KINDS = ['user', 'system', 'super_admin'] as const;
export type ViolationEventActorKind = (typeof VIOLATION_EVENT_ACTOR_KINDS)[number];

// ---------- Sources (adapter IDs) ----------

/**
 * The canonical adapter identifier list. Each value matches
 * `ViolationSourceAdapter.id` in the worker. New sources require a
 * coordinated entry here + a registry binding in the worker.
 */
export const VIOLATION_SOURCE_IDS = [
  'nyc_hpd_open_data',
  'nyc_hpd_complaints_open_data',
  'nyc_dob_open_data',
  'nyc_dob_ecb_open_data',
  'nyc_oath_open_data',
  'nyc_fdny_open_data',
  'nyc_311_open_data',
  'nyc_dob_bis_scraper',
] as const;
export type ViolationSourceId = (typeof VIOLATION_SOURCE_IDS)[number];

export interface ViolationSourceDescriptor {
  id: ViolationSourceId;
  agency: ViolationAgency;
  displayName: string;
  /** Surface used for capability discovery. The actual fetch capability
   *  lives on the adapter; this metadata mirrors it for the UI. */
  canLookupByBin: boolean;
  canLookupByBbl: boolean;
  canLookupByAddress: boolean;
  /** Default poll interval in milliseconds. Overridable per-org via
   *  `org_settings.violations.pollingIntervalOverrides[sourceId]`. */
  defaultPollIntervalMs: number;
  /** Whether this source uses the Playwright fallback. Surfaces in the
   *  source-health UI so super-admins can see scrapers separately. */
  isScraper: boolean;
}

const MIN = 60 * 1000;
const HOUR = 60 * MIN;

export const VIOLATION_SOURCE_DESCRIPTORS: Record<ViolationSourceId, ViolationSourceDescriptor> = {
  nyc_hpd_open_data: {
    id: 'nyc_hpd_open_data',
    agency: 'HPD',
    displayName: 'HPD Violations (NYC Open Data)',
    canLookupByBin: true,
    canLookupByBbl: true,
    canLookupByAddress: false,
    defaultPollIntervalMs: 30 * MIN,
    isScraper: false,
  },
  nyc_hpd_complaints_open_data: {
    id: 'nyc_hpd_complaints_open_data',
    agency: 'HPD',
    displayName: 'HPD Complaints (NYC Open Data)',
    canLookupByBin: true,
    canLookupByBbl: true,
    canLookupByAddress: false,
    defaultPollIntervalMs: 30 * MIN,
    isScraper: false,
  },
  nyc_dob_open_data: {
    id: 'nyc_dob_open_data',
    agency: 'DOB',
    displayName: 'DOB Violations (NYC Open Data)',
    canLookupByBin: true,
    canLookupByBbl: true,
    canLookupByAddress: false,
    defaultPollIntervalMs: HOUR,
    isScraper: false,
  },
  nyc_dob_ecb_open_data: {
    id: 'nyc_dob_ecb_open_data',
    agency: 'ECB',
    displayName: 'DOB / ECB Violations (NYC Open Data)',
    canLookupByBin: true,
    canLookupByBbl: false,
    canLookupByAddress: false,
    defaultPollIntervalMs: HOUR,
    isScraper: false,
  },
  nyc_oath_open_data: {
    id: 'nyc_oath_open_data',
    agency: 'OATH',
    displayName: 'OATH Hearings (NYC Open Data)',
    canLookupByBin: false,
    canLookupByBbl: true,
    canLookupByAddress: false,
    defaultPollIntervalMs: HOUR,
    isScraper: false,
  },
  nyc_fdny_open_data: {
    id: 'nyc_fdny_open_data',
    agency: 'FDNY',
    displayName: 'FDNY Active Violations (NYC Open Data)',
    canLookupByBin: true,
    canLookupByBbl: false,
    canLookupByAddress: true,
    defaultPollIntervalMs: 4 * HOUR,
    isScraper: false,
  },
  nyc_311_open_data: {
    id: 'nyc_311_open_data',
    agency: '_311',
    displayName: '311 Service Requests (NYC Open Data)',
    canLookupByBin: false,
    canLookupByBbl: true,
    canLookupByAddress: true,
    defaultPollIntervalMs: 2 * HOUR,
    isScraper: false,
  },
  nyc_dob_bis_scraper: {
    id: 'nyc_dob_bis_scraper',
    agency: 'DOB',
    displayName: 'DOB BIS (Web fallback)',
    canLookupByBin: false,
    canLookupByBbl: false,
    canLookupByAddress: true,
    defaultPollIntervalMs: HOUR,
    isScraper: true,
  },
};

/**
 * Sources to seed for buildings in a given borough. Today every NYC
 * borough gets the same list; non-NYC buildings get nothing. Calling
 * with `null` (no resolved borough) returns an empty array so the
 * scheduler skips it cleanly — matches the verification checklist
 * "violations from buildings outside NYC gracefully skip without
 * errors".
 */
export function violationSourcesForBorough(borough: Borough | null): ViolationSourceId[] {
  if (!borough) return [];
  return [...VIOLATION_SOURCE_IDS];
}

// ---------- Polling cadence + quiet hours ----------

export const VIOLATIONS_DISPATCH_INTERVAL_MS = 5 * MIN;
/** Overlap window applied to `since` filters so retroactive agency
 *  edits are caught even after the first poll observes them. */
export const VIOLATIONS_FETCH_OVERLAP_MS = 24 * HOUR;

export interface QuietHoursWindow {
  /** 0–23 inclusive. Quiet starts at this hour, runs through `endHour`
   *  exclusive. Defaults match spec: 23 → 06 ET. */
  startHour: number;
  endHour: number;
  /** IANA timezone name. Default New York. */
  timezone: string;
}

export const DEFAULT_VIOLATIONS_QUIET_HOURS: QuietHoursWindow = {
  startHour: 23,
  endHour: 6,
  timezone: 'America/New_York',
};

/**
 * Returns `true` when the supplied instant falls within the org's quiet
 * window, given the IANA timezone. Pure: callers pass `now` so tests
 * can pin the clock without monkey-patching.
 */
export function isWithinQuietHours(now: Date, window: QuietHoursWindow): boolean {
  const hour = hourInTimezone(now, window.timezone);
  if (window.startHour === window.endHour) return false;
  if (window.startHour < window.endHour) {
    return hour >= window.startHour && hour < window.endHour;
  }
  // Wrap-around (e.g. 23 → 06): match either side of midnight.
  return hour >= window.startHour || hour < window.endHour;
}

function hourInTimezone(now: Date, timezone: string): number {
  // `Intl.DateTimeFormat` resolves the local hour for a given tz without
  // pulling in a date library. The `hourCycle: 'h23'` option pins the
  // result to 0–23 across runtimes.
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    hourCycle: 'h23',
  });
  const parts = fmt.formatToParts(now);
  const hourStr = parts.find((p) => p.type === 'hour')?.value ?? '0';
  const parsed = Number.parseInt(hourStr, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

// ---------- Circuit breaker ----------

/** Number of consecutive global failures (across all buildings using a
 *  given source) before the breaker trips. Per-building failures don't
 *  count — only failures at the source level. */
export const VIOLATION_SOURCE_FAILURE_THRESHOLD = 5;
/** Cooldown period after the breaker trips before automatic retries
 *  resume. */
export const VIOLATION_SOURCE_COOLDOWN_MS = 60 * MIN;

// ---------- Auto-WO defaults ----------

export const DEFAULT_AUTO_WO_DUE_DAYS = 14;

// ---------- Severity → tone mapping for the UI ----------

export type ViolationTone = 'critical' | 'high' | 'medium' | 'low' | 'muted' | 'success' | 'info';

export function violationTone(args: {
  status: ViolationStatus;
  severity: ViolationSeverity;
  cureByAt?: Date | string | null;
  now?: Date;
}): ViolationTone {
  if (args.status === 'closed') return 'success';
  if (args.status === 'dismissed') return 'muted';
  // Overdue cure date elevates the tone visually even for medium-severity
  // items. Matches the "cure-by countdown (red when overdue)" callout in
  // the spec.
  const cureBy =
    args.cureByAt == null
      ? null
      : args.cureByAt instanceof Date
        ? args.cureByAt
        : new Date(args.cureByAt);
  if (cureBy && cureBy.getTime() < (args.now ?? new Date()).getTime()) {
    return 'critical';
  }
  switch (args.severity) {
    case 'critical':
      return 'critical';
    case 'high':
      return 'high';
    case 'medium':
      return 'medium';
    case 'low':
      return 'low';
    case 'info':
    default:
      return 'info';
  }
}
